import type { DashboardDataSource } from '../../dataSource'
import { getEffectiveSlicerPanel, toPreviousYearDatePresetFilters } from '../../runtime'
import type { DrillOrder } from '../../drilldownHierarchy'
import type { DashboardWidget } from '../../types'
import { createWidgetIntentKey } from './intent'
import {
  buildSlicerOptionsInput,
  buildSlicerOptionsRequestKey,
  buildWidgetDrilldownInput,
  buildWidgetHydrateInput,
} from './requestBuilders'
import { selectHydratableWidgetIds } from './selectors'
import type { DashboardGlobalFilterState, DashboardViewAction, DashboardViewState, WidgetRequestCause, WidgetRequestIntent } from './state'
import { buildWidgetSessionDataFromResult } from './state'

function shiftDateInputYear(value: string | null, deltaYears: number) {
  if (!value) return null
  const [yearRaw, monthRaw, dayRaw] = value.split('-')
  const year = Number(yearRaw)
  const month = Number(monthRaw)
  const day = Number(dayRaw)
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null

  const shifted = new Date(year + deltaYears, month - 1, day)
  if (shifted.getMonth() !== month - 1) {
    shifted.setDate(0)
  }

  const nextYear = shifted.getFullYear()
  const nextMonth = String(shifted.getMonth() + 1).padStart(2, '0')
  const nextDay = String(shifted.getDate()).padStart(2, '0')
  return `${nextYear}-${nextMonth}-${nextDay}`
}

// Custom ranges are shifted back a year here. A preset keeps the same applied
// filters; its datePreset filter is re-sent with Filter 'previousYear' so the
// API shifts the model's own window (see toPreviousYearDatePresetFilters).
function buildPreviousYearFilters(appliedFilters: DashboardGlobalFilterState): DashboardGlobalFilterState | null {
  const { dateRange } = appliedFilters
  if (dateRange.mode === 'preset') {
    if (!dateRange.preset) return null
    return {
      slicerValues: { ...appliedFilters.slicerValues },
      dateRange: { ...dateRange },
    }
  }

  if (dateRange.mode !== 'custom') return null
  const from = shiftDateInputYear(dateRange.from, -1)
  const to = shiftDateInputYear(dateRange.to, -1)
  if (!from || !to) return null

  return {
    slicerValues: { ...appliedFilters.slicerValues },
    dateRange: { ...dateRange, from, to },
  }
}

type CoordinatorDeps = {
  clientId: string
  dataSource: DashboardDataSource
  /** Resolves the widget's drill order (override array) for drill requests. */
  getWidgetDrillOrder?: (widget: DashboardWidget) => Pick<DrillOrder, 'mode' | 'dimensions'> | undefined
  dispatch: (action: DashboardViewAction) => DashboardViewState
  getState: () => DashboardViewState
}

export type DashboardRequestCoordinator = {
  loadSlicerOptions(options?: { onlyChanged?: boolean }): Promise<void>
  refreshAllWidgets(cause: Extract<WidgetRequestCause, 'initial-load' | 'global-filter-apply' | 'refresh-all' | 'view-apply'>): Promise<void>
  runWidgetIntent(intent: WidgetRequestIntent): Promise<void>
}

export function createDashboardRequestCoordinator({
  clientId,
  dataSource,
  getWidgetDrillOrder,
  dispatch,
  getState,
}: CoordinatorDeps): DashboardRequestCoordinator {
  async function loadSlicerOptions({ onlyChanged = false }: { onlyChanged?: boolean } = {}) {
    const state = getState()
    const slicers = getEffectiveSlicerPanel(state.dashboard)?.slicers ?? []
    if (!state.dashboard || slicers.length === 0) {
      return
    }

    // Each slicer's request excludes its own filter, so after an apply only the
    // slicers whose other-slicer scope changed need new options. Requests whose
    // key matches the last one started (loaded or in flight) are skipped; a
    // field whose last load failed is always retried.
    const { requestKeyByField, errorByField } = state.slicerOptions
    const requests = slicers.flatMap((slicer) => {
      const input = buildSlicerOptionsInput(state, slicer.field, clientId)
      if (!input) return []
      const requestKey = buildSlicerOptionsRequestKey(input)
      if (onlyChanged && requestKeyByField[slicer.field] === requestKey && !errorByField[slicer.field]) return []
      return [{ field: slicer.field, input, requestKey }]
    })
    if (requests.length === 0) {
      return
    }

    dispatch({
      type: 'SLICER_OPTIONS_LOAD_STARTED',
      requestKeyByField: Object.fromEntries(requests.map(({ field, requestKey }) => [field, requestKey])),
    })

    await Promise.all(requests.map(async ({ field, input, requestKey }) => {
      try {
        const options = await dataSource.slicerOptions(input)
        dispatch({
          type: 'SLICER_OPTIONS_LOAD_SUCCEEDED',
          field,
          options,
          requestKey,
        })
      } catch (error) {
        dispatch({
          type: 'SLICER_OPTIONS_LOAD_FAILED',
          field,
          error: error instanceof Error ? error.message : 'Failed to load slicer options.',
          requestKey,
        })
      }
    }))
  }

  async function refreshAllWidgets(cause: Extract<WidgetRequestCause, 'initial-load' | 'global-filter-apply' | 'refresh-all' | 'view-apply'>) {
    const state = getState()
    const widgetIds = selectHydratableWidgetIds(state)
    await Promise.all(widgetIds.map(async (widgetId) => {
      const nextState = getState()
      const session = nextState.widgets[widgetId]
      if (!session) return

      await runWidgetIntent({
        widgetId,
        cause,
        query: {
          ...session.query,
          drilldown: session.query.drilldown ? { ...session.query.drilldown } : null,
        },
        appliedFilters: {
          slicerValues: { ...nextState.appliedFilters.slicerValues },
          dateRange: { ...nextState.appliedFilters.dateRange },
        },
      })
    }))
  }

  async function runWidgetIntent(intent: WidgetRequestIntent): Promise<void> {
    const state = getState()
    const session = state.widgets[intent.widgetId]
    if (!state.dashboard || !session) {
      return
    }

    const intentKey = createWidgetIntentKey(intent)
    if (intentKey === session.request.lastCompletedIntentKey) {
      return
    }

    if (session.request.inFlight) {
      dispatch({
        type: 'WIDGET_REQUEST_QUEUED',
        widgetId: intent.widgetId,
        intent,
      })
      return
    }

    const requestId = session.request.requestId + 1
    dispatch({
      type: 'WIDGET_REQUEST_STARTED',
      widgetId: intent.widgetId,
      requestId,
      intentKey,
    })

    try {
      const latestState = getState()
      const latestSession = latestState.widgets[intent.widgetId]
      if (!latestSession) {
        return
      }

      const result = intent.query.drilldown
        ? await (async () => {
            const drillWidget = latestState.dashboard?.widgets.find((item) => item.id === intent.widgetId)
            const input = buildWidgetDrilldownInput(
              latestState,
              intent.widgetId,
              intent,
              clientId,
              drillWidget ? getWidgetDrillOrder?.(drillWidget) : undefined,
            )
            if (!input) throw new Error('Widget drilldown input could not be built.')
            if (!dataSource.drilldownWidget) throw new Error('Drilling down isn’t available here.')
            return dataSource.drilldownWidget(input)
          })()
        : await (async () => {
            const input = buildWidgetHydrateInput(latestState, intent.widgetId, intent, clientId)
            if (!input) throw new Error('Widget hydrate input could not be built.')
            return dataSource.hydrateWidget(input)
          })()

      const widget = latestState.dashboard?.widgets.find((item) => item.id === intent.widgetId)
      const previousYearFilters = widget?.type === 'line' && widget.comparePreviousYear && !intent.query.drilldown
        ? buildPreviousYearFilters(intent.appliedFilters)
        : null
      const comparisonResult = previousYearFilters
        ? await (async () => {
            const input = buildWidgetHydrateInput(latestState, intent.widgetId, {
              ...intent,
              appliedFilters: previousYearFilters,
              query: {
                ...intent.query,
                page: 1,
              },
            }, clientId)
            if (!input) throw new Error('Previous year widget hydrate input could not be built.')
            const effectiveDaxGenerationRequest = input.effectiveDaxGenerationRequest
            return dataSource.hydrateWidget({
              ...input,
              effectiveDaxGenerationRequest: effectiveDaxGenerationRequest && previousYearFilters.dateRange.mode === 'preset'
                ? {
                    ...effectiveDaxGenerationRequest,
                    filters: toPreviousYearDatePresetFilters(effectiveDaxGenerationRequest.filters ?? []),
                  }
                : effectiveDaxGenerationRequest,
              page: 1,
              pageSize: intent.query.pageSize,
              applyPreviewRowLimit: false,
            })
          })()
        : null

      const afterResponse = getState().widgets[intent.widgetId]
      const queuedIntent = afterResponse?.request.queuedIntent
      if (!afterResponse || afterResponse.request.requestId !== requestId) {
        return
      }
      if (queuedIntent && createWidgetIntentKey(queuedIntent) !== intentKey) {
        return
      }

      dispatch({
        type: 'WIDGET_REQUEST_SUCCEEDED',
        widgetId: intent.widgetId,
        requestId,
        intentKey,
        data: {
          ...buildWidgetSessionDataFromResult(result),
          comparisonChartData: comparisonResult?.chartData ?? [],
        },
      })
    } catch (error) {
      const afterError = getState().widgets[intent.widgetId]
      const queuedIntent = afterError?.request.queuedIntent
      if (!afterError || afterError.request.requestId !== requestId) {
        return
      }
      if (queuedIntent && createWidgetIntentKey(queuedIntent) !== intentKey) {
        return
      }

      dispatch({
        type: 'WIDGET_REQUEST_FAILED',
        widgetId: intent.widgetId,
        requestId,
        intentKey,
        error: error instanceof Error ? error.message : 'Failed to fetch widget data.',
      })
    } finally {
      const latest = getState().widgets[intent.widgetId]
      dispatch({
        type: 'WIDGET_REQUEST_FINISHED',
        widgetId: intent.widgetId,
        requestId,
      })

      const nextQueuedIntent = getState().widgets[intent.widgetId]?.request.queuedIntent
      if (latest?.request.requestId === requestId && nextQueuedIntent) {
        await runWidgetIntent(nextQueuedIntent)
      }
    }
  }

  return {
    loadSlicerOptions,
    refreshAllWidgets,
    runWidgetIntent,
  }
}
